import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";

import store from "./redux/store";
import { clearAllStorage, getAccessToken, setOrgaizationSettings } from "./utils/storage";

import AppLayout from "./AppLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/toastContext";

// Screens
import Login from "./screens/Login";
import Signup from "./screens/Signup";
import RegistrationComplete from "./screens/RegistrationSuccess";
import Dashboard from "./screens/Dashboard";
import Settings from "./Settings";
import Cards from "./Cards";
import Wallets from "./screens/Wallets";
import WalletTransfer from "./WalletTransfer";
import WalletFunding from "./WalletFunding";
import WalletConvert from "./WalletConvert";
import CryptoTransfer from "./CryptoTransfer";
import Transactions from "./Transactions";
import Payment from "./Payment";
import Bills from "./Bills";
import Profile from "./Profile";
import ProtectedRoute from "./api/protectedRoutes";
import { useOrganizationSettings } from "./api/hooks/organizationSettings";
import LoanCenter from "./screens/LoanCenter";
import LoanSuccess from "./screens/LoanSuccess";
import BookLoan from "./screens/BookLoan";
import InvestmentPage from "./screens/Investment";
import EProductsPage from "./screens/eProducts";
import SuccessPage from "./screens/SuccessPage";
import LiquidationRequest from "./screens/LiquidationRequest";
import AutoLogout from "./components/autoLogout";
import { initCompanyFromSubdomain } from "./utils/utils";

function App() {
  const [token, setToken] = useState(getAccessToken());

  const { data, isLoading } = useOrganizationSettings();

  const handleLogout = () => {
    console.log("User logged out due to inactivity.");
    // Example: clear auth token & redirect
    localStorage.removeItem("authToken");
    window.location.href = "/login";
  };

  useEffect(() => {
    console.log("App mounted, token:", token);
  }, [token]);

  const queryClient = new QueryClient();


  useEffect(() => {
    const fetchData = async () => {
      try {
        const companyInfo = await initCompanyFromSubdomain();
        // const companyInfo = await initCompanyFromSubdomain("darpay.gbi.ng");
        // const companyInfo = await initCompanyFromSubdomain("menielink.gbi.ng");
        // const companyInfo = await initCompanyFromSubdomain("SONSCMSapp.gbi.ng");

        console.log(companyInfo, "Main Company Details");
        if (companyInfo == null) {
          clearAllStorage();
        }

        setOrgaizationSettings(companyInfo);

      } catch (err) {

        clearAllStorage();

        // Save everything
        sessionStorage.setItem("beller_app_id", "");
        sessionStorage.setItem("company", "");
        sessionStorage.setItem("company_key", ""); // track current company source
        console.log("Error fetching:", err);
      }
    };

    fetchData();

  }, [])

  return (
    <Provider store={store}>
      <ErrorBoundary>
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <Router>
              <AutoLogout onLogout={handleLogout} />

              <Routes>

                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route
                  path="/signup-success"
                  element={<RegistrationComplete />}
                />

                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  <Route
                    path="/dashboard"
                    element={
                      <AppLayout>
                        <Dashboard />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/cards"
                    element={
                      <AppLayout>
                        <Cards />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/wallets"
                    element={
                      <AppLayout>
                        <Wallets />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/wallet-transfer"
                    element={
                      <AppLayout>
                        <WalletTransfer />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/wallet-transfer/:sourceWalletId"
                    element={
                      <AppLayout>
                        <WalletTransfer />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/wallet-funding"
                    element={
                      <AppLayout>
                        <WalletFunding />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/wallet-funding/:sourceWalletId"
                    element={
                      <AppLayout>
                        <WalletFunding />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/wallet-convert"
                    element={
                      <AppLayout>
                        <WalletConvert />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/wallet-convert/:sourceWalletId"
                    element={
                      <AppLayout>
                        <WalletConvert />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/crypto-transfer"
                    element={
                      <AppLayout>
                        <CryptoTransfer />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/crypto-transfer/:sourceWalletId"
                    element={
                      <AppLayout>
                        <CryptoTransfer />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <AppLayout>
                        <Settings />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <AppLayout>
                        <Profile />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/transactions"
                    element={
                      <AppLayout>
                        <Transactions />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/payment"
                    element={
                      <AppLayout>
                        <Payment />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/loan-center"
                    element={
                      <AppLayout>
                        <LoanCenter />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/book-loan"
                    element={
                      <AppLayout>
                        <BookLoan />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/liquidation-request"
                    element={
                      <AppLayout>
                        <LiquidationRequest />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/success-page"
                    element={
                      <AppLayout>
                        <SuccessPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/investments"
                    element={
                      <AppLayout>
                        <InvestmentPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/e-products"
                    element={
                      <AppLayout>
                        <EProductsPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/loan-application-success"
                    element={
                      <AppLayout>
                        <LoanSuccess />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/bills"
                    element={
                      <AppLayout>
                        <Bills />
                      </AppLayout>
                    }
                  />
                </Route>

                {/* Catch-all Redirect */}
                <Route
                  path="*"
                  element={
                    <Navigate to={token ? "/dashboard" : "/login"} replace />
                  }
                />
              </Routes>
            </Router>
          </QueryClientProvider>
        </ToastProvider>
      </ErrorBoundary>
    </Provider>
  );
}

export default App;
